import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useSupabase } from "@how/app-kit";

/*
 * My schedule in the member portal (spec 2026-10-08 piece 3; ADR 0070). The one cached query is the signed-in person's
 * own list from every church where they serve (my_schedule_everywhere): user-scoped, no church's other data. Answers
 * and blockouts go to each church's own functions, unchanged; the page answers only the church being viewed (R3).
 */

const churchSchema = z.object({ tenantId: z.string(), slug: z.string(), name: z.string() });
const assignmentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  timeZone: z.string(),
  location: z.string().nullable(),
  team: z.string(),
  position: z.string(),
  response: z.enum(["no_reply", "accepted", "declined"]),
  note: z.string().nullable(),
  isCancelled: z.boolean(),
  season: z.string().nullable(),
  message: z.string().nullable(),
  conflict: z.boolean(),
});
const blockoutSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  reason: z.string().nullable(),
});
const scheduleSchema = z.object({
  churches: z.array(churchSchema),
  assignments: z.array(assignmentSchema),
  blockouts: z.array(blockoutSchema),
  needsTwoStep: z.array(z.object({ slug: z.string(), name: z.string() })),
  sharing: z.object({ choice: z.boolean().nullable(), askedAt: z.string().nullable() }),
});
export type MyScheduleEverywhere = z.infer<typeof scheduleSchema>;
export type ScheduleChurch = z.infer<typeof churchSchema>;
export type ScheduleAssignment = z.infer<typeof assignmentSchema>;
export type ScheduleBlockout = z.infer<typeof blockoutSchema>;

export function useMyScheduleEverywhere(enabled = true) {
  const supabase = useSupabase();
  return useQuery({
    queryKey: ["my-schedule-everywhere"],
    enabled,
    retry: false,
    queryFn: async (): Promise<MyScheduleEverywhere> => {
      const { data, error } = await supabase.rpc("my_schedule_everywhere");
      if (error) throw error;
      return scheduleSchema.parse(data);
    },
  });
}

/** After any change: the list, and Home's waiting card (my_schedule, every church key under "my-schedule"). */
function useRefresh() {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["my-schedule-everywhere"] }),
      client.invalidateQueries({ queryKey: ["my-schedule"] }),
    ]);
  };
}

export function useRespond() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: { assignmentId: string; accepted: boolean; note?: string }) => {
      const { error } = await supabase.rpc("respond_to_assignment", {
        p_assignment_id: input.assignmentId,
        p_accepted: input.accepted,
        ...(input.note === undefined || input.note.trim() === ""
          ? {}
          : { p_note: input.note.trim() }),
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });
}

/**
 * One blockout per chosen church, each through that church's own set_blockout (ADR 0066; kept by ruling S1); one
 * church refusing does not stop the rest, and the result says which were saved.
 */
export function useSetBlockouts() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: {
      tenantIds: readonly string[];
      startsOn: string;
      endsOn: string;
      reason: string;
    }) => {
      const saved: string[] = [];
      const failed: { tenantId: string; message: string; code: string }[] = [];
      for (const tenantId of input.tenantIds) {
        const { error } = await supabase.rpc("set_blockout", {
          p_tenant_id: tenantId,
          p_starts_on: input.startsOn,
          p_ends_on: input.endsOn,
          ...(input.reason.trim() === "" ? {} : { p_reason: input.reason.trim() }),
        });
        if (error) failed.push({ tenantId, message: error.message, code: error.code });
        else saved.push(tenantId);
      }
      return { saved, failed };
    },
    onSettled: refresh,
  });
}

export function useDeleteBlockout() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (blockoutId: string) => {
      const { error } = await supabase.rpc("delete_blockout", { p_blockout_id: blockoutId });
      if (error) throw error;
    },
    onSuccess: refresh,
  });
}

export function useSetBusySharing() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (share: boolean) => {
      const { error } = await supabase.rpc("set_busy_sharing", { p_share: share });
      if (error) throw error;
    },
    onSuccess: refresh,
  });
}

export function useMarkAsked() {
  const supabase = useSupabase();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("mark_busy_sharing_asked");
      if (error) throw error;
    },
    onSuccess: refresh,
  });
}
